import { z } from "zod";
import { facets } from "../facets/facets.js";
const trimString = (u) => (typeof u === "string" ? u.trim() : u);
const matchInsensitiveString = (value, lower) => value.toLowerCase() === lower;
const insensitive = (lower) =>
  z.string().refine((str) => matchInsensitiveString(str, lower));
/** **************************
 * Ask questions, receive answers, parse json.
 * */
export const askQuestion = () =>
  z.object({
    text: z.preprocess(trimString, z.string()),
  });
// The pain of keeping this in sync with the implemented response
// pays off in not needing unit tests for the API layer. ..right?
export const receiveAnswer = () =>
  z.object({
    data: z.array(z.any()),
    meta: z.object({
      query: z.string(),
      data: z.object({
        is: z.string(),
        canBe: z.array(z.string()),
        explain: z.object({
          keys: z.array(z.string()),
          strings: z.array(z.string()),
          numbers: z.array(z.string()),
          unknowns: z.array(z.string()),
        }),
      }),
      ast: z.object({
        entities: z.array(
          z.object({
            name: z.string(),
            type: z.string(),
          }),
        ),
        returns: z.array(
          z.union([
            z.object({
              as: z.string(),
            }),
            z.object({
              as: z.string(),
              statement: z.string(),
            }),
            z.object({
              as: z.string(),
              entity: z.string(),
              attribute: z.string(),
            }),
          ]),
        ),
        order_by: z
          .object({
            as: z.string(),
            direction: z.string().optional(),
          })
          .optional(),
        limit: z.number().optional(),
      }),
      suggestion: z.union([
        z.object({
          type: z.literal("time-series"),
          by: z.literal("day"),
        }),
        z.object({
          type: z.literal("unknown"),
        }),
      ]),
    }),
  });
export const receiveQuestionQuery = () =>
  z.union([
    z.object({
      intent: z.literal("query"),
      query: graphSearchQuery(),
    }),
    z.object({
      intent: z.literal("search"),
      query: graphSearchQuery(),
    }),
    z.object({
      intent: z.literal("analysis"),
      topicName: z.string(),
    }),
    z.object({
      intent: z.literal("team"),
      teamName: z.string().optional(),
      teamUuid: z.string().optional(),
    }),
  ]);
export const askFeedback = () =>
  z.object({
    satisfying: z.boolean(),
    producer: z.enum(["askForQuery", "askAnything"]).optional(),
    prompt: z.string(),
    response: z.string(),
  });
/** *******************
 *  Auto-complete stuff
 * */
export const receiveLabelProperties = () =>
  z.array(
    z.object({
      label: z.string(),
      properties: z.array(z.string()),
    }),
  );
export const labelPropertyValues = () =>
  z.object({
    label: z.string(),
    property: z.string(),
    search: z.string().optional(),
    page: z.number().optional().default(1),
    pageSize: z.number().optional().default(100),
  });
export const receiveLabelPropertyValues = () => z.array(z.string());
const people = () =>
  z.object({
    identifier: z.string(),
    uuid: z.string().uuid(),
    integration: z.string(),
    type: z.string().optional().default(""),
    name: z.string().optional().default(""),
    email: z.string().optional().default(""),
  });
export const receivePeople = () => z.array(people());
export const receiveIdentities = () =>
  z.array(
    z.object({
      identifier: z.string(),
      uuid: z.string().uuid(),
      integration: z.string(),
      type: z.string().optional().default(""),
      name: z.string().optional().default(""),
      email: z.string().optional().default(""),
      mergedEntities: z
        .array(
          z.object({
            uuid: z.string(),
            state: z.string(),
            properties: z.union([people(), z.object({ uuid: z.string() })]),
          }),
        )
        .optional(),
    }),
  );
export const receiveProposals = () => z.array(receiveIdentities());
/** **************************
 * entity resolution
 * */
export const resolutionJob = () =>
  z.object({
    orgId: z.string().uuid(),
    jobId: z.string().uuid(),
    uuids: z.array(z.string().uuid()).optional().default([]),
    resolveToUuid: z.string().uuid().optional(),
    unresolveFromUuid: z.string().uuid().optional(),
    type: z.literal("Person"),
  });
export const receiveResolutionJob = () =>
  z.object({
    status: z.union([
      z.literal("none"),
      z.literal("job::running"),
      z.literal("job::submitted"),
      z.literal("done::success"),
      z.literal("done::failure"),
      z.literal("error::input"),
      z.literal("error::load"),
    ]),
  });
const stringToDate = z
  .string()
  .datetime({ offset: true })
  .pipe(z.coerce.date());
/******
 * graph config
 * */
export const integrationConfiguration = () =>
  z.object({
    integrations: z.array(
      z.object({
        name: z.string(),
        roles: z.array(z.string()),
        modes: z.array(z.string()),
        provides: z.array(z.string()),
        rolesProvide: z.record(z.string(), z.array(z.string())).optional(),
        check: z
          .object({
            latestHits: z.record(z.string(), stringToDate),
            erroredAt: z.array(stringToDate),
            updatedAt: stringToDate,
          })
          .optional(),
        ingested: z
          .array(
            z.object({
              entityType: z.string(),
              lastIngestionDate: z.optional(stringToDate),
              firstIngestionDate: z.optional(stringToDate),
              count: z.number(),
            }),
          )
          .optional(),
        ingestionStats: z
          .object({
            total: z.number(),
            latest: z.number(),
            earliest: z.number(),
          })
          .optional(),
      }),
    ),
    upwardRels: z.record(z.string(), z.array(z.string())),
    downwardRels: z.record(z.string(), z.array(z.string())),
    schemaInfo: z.record(
      z.string(),
      z.array(
        z.object({
          attribute: z.string(),
          target: z.string(),
          type: z.string().optional(),
          canonical: z.boolean().optional(),
          groupable: z.boolean().optional(),
          projected: z.boolean().optional(),
          machineOnly: z.boolean().optional(),
          many: z.boolean().optional(),
          unit: z.string().optional(),
        }),
      ),
    ),
    populatedEntities: z.array(z.string()).optional(),
    entityDiagnostics: z
      .record(
        z.string(),
        z.object({
          entity: z.string(),
          providable: z.boolean(),
          populated: z.boolean().optional(),
          providedByConfiguredIntegration: z.boolean().optional(),
          providedByConfiguredIntegrationRole: z.boolean().optional(),
          reasons: z
            .array(
              z.object({
                code: z.string(),
                integration: z.string(),
                role: z.string().optional(),
              }),
            )
            .optional(),
        }),
      )
      .optional(),
  });
/** **************************
 * unused, misfired, should remove or updated
 * */
export const eventLookup = () =>
  z.object({ orgId: z.string().uuid(), identifier: z.string() });
const target = z.string();
const field = z.object({
  target: target,
  attribute: z.string(),
});
const fieldValue = z.string();
const comparison = z.union([
  z.object({
    IN: z.array(fieldValue),
  }),
  z.object({
    "NOT IN": z.array(fieldValue),
  }),
  z.object({
    WITHIN: z.array(fieldValue),
  }),
]);
export const ConstrainedField = z.object({
  target: target,
  attribute: z.string(),
  comparison,
});
const aggregatedField = z.object({
  target: target,
  attribute: z.string(),
  reducer: z.string(),
  filters: z.array(ConstrainedField).optional(),
});
const orderBy = z.union([
  z.string(),
  z.object({
    target: target,
    attribute: z.string(),
    reducer: z.optional(z.string()),
  }),
]);
const formula = z
  .tuple([z.string()])
  .rest(z.union([z.string(), z.lazy(() => formula)]));
const columnFormula = z.object({
  name: z.string().optional(),
  formula: formula,
});
const columnHaving = z.object({
  column: z.string(),
  comparison,
});
/** **************************
 * explore inputs and outputs
 * */
export const graphSearchQuery = () =>
  z.object({
    orgId: z.string().uuid(),
    page: z.optional(z.number()),
    pageSize: z.optional(z.number()),
    startDate: z.optional(z.string().datetime({ offset: true })),
    endDate: z.optional(z.string().datetime({ offset: true })),
    type: z.optional(z.string()),
    duration: z.optional(z.string()),
    search: z.optional(z.string()),
    select: z.optional(z.array(z.string())),
    columns: z.optional(z.array(z.string())),
    groupBys: z.optional(z.array(field)),
    filters: z.optional(z.array(ConstrainedField)),
    aggregations: z.optional(z.array(aggregatedField)),
    formulas: columnFormula.array().optional(),
    having: columnHaving.array().optional(),
    order: z.optional(z.array(z.object({ by: orderBy, direction }))),
    limit: z.optional(z.number()),
    reporter: z.optional(z.string()),
    prompt: z.optional(z.string()),
    timezone: z.optional(z.string()),
    timeUnit: z.optional(z.enum(["days", "hours", "minutes"])),
    compareWith: z.optional(
      z.object({
        ago: z.string(),
        method: z.string(),
      }),
    ),
    format: z.optional(z.enum(["json", "csv"])),
  });
export const digestPromptQuery = () =>
  z.object({
    snippet: z.optional(graphSearchQuery()),
    sections: z.array(
      z.object({
        prompt: z.string(),
        temperature: z.optional(z.number()),
        queries: z.array(graphSearchQuery()),
      }),
    ),
  });
const direction = z.union([
  insensitive("asc"),
  insensitive("ascending"),
  insensitive("dsc"),
  insensitive("desc"),
  insensitive("descending"),
]);
export const listSchema = () => {
  return z.record(
    z.union([z.string(), z.array(z.string()), z.number()]).nullable(),
  );
};
export const eventSchema = () => {
  const facetValueSchema = () =>
    z
      .union([
        z.string(),
        z.number(),
        z.boolean(),
        z.array(z.union([z.string(), z.number()])),
      ])
      .optional();
  const attributes = {};
  facets.forEach((facet) => (attributes[facet] = facetValueSchema()));
  return z.object({
    identifier: z.string(),
    attributes: z.object({
      description: z.string().default(""),
      integration: z.string(),
      name: z.string().default(""),
      occurredAt: z.string().datetime({ offset: true }),
      sourceUrl: z.string().default(""),
      type: z.string().default(""),
      uuid: z.string().uuid(),
      entityUuid: z.string().uuid().optional(),
      ...attributes,
      actor: z.object({
        uuid: z.string().uuid(),
        avatarUrl: z.string().default(""),
        email: z.string().default(""),
        identifier: z.string().default(""),
        login: z.string().default(""),
        name: z.string().default(""),
      }),
    }),
  });
};
export const entitySchema = () => {
  const propertiesValuesSchema = () =>
    z
      .union([z.string(), z.array(z.string()), z.record(z.string())])
      .optional()
      .nullish();
  const properties = z.record(propertiesValuesSchema());
  return z.object({
    identifier: z.string(),
    attributes: z
      .object({
        uuid: z.string().uuid(),
        entityUuid: z.string().optional(),
      })
      .and(properties),
  });
};
/** **************************
 * common schemas
 * */
const columnInfo = z.array(
  z.object({
    key: z.string(),
    type: z.string(),
    unit: z.string().optional(),
    target: z.string(),
    attribute: z.string(),
    reducer: z.string().optional(),
    diffMethod: z.string().optional(),
  }),
);
const explanation = z.array(
  z.object({
    entity: z.string(),
    providable: z.boolean(),
    populated: z.boolean().optional(),
    reasons: z
      .array(
        z.object({
          code: z.string(),
          integration: z.string(),
          role: z.string().optional(),
        }),
      )
      .optional(),
  }),
);
export const paginatedResponseSchema = (schema) =>
  // Integrations does not like the next schema, and I do not know why yet.
  // In order to compile, we need to satifisy both compilers. This two-liner does it.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  z.object({
    data: z.union([z.array(schema), z.string()]),
    meta: z.object({
      columnInfo,
      page: z.coerce.number().default(1),
      pageSize: z.coerce.number().default(20),
      total: z.coerce.number().default(0),
      explanation: explanation.optional(),
    }),
  });
export const timeSeriesResponseSchema = z.object({
  data: z.array(z.tuple([z.string(), z.number()])),
  meta: z.object({
    explanation: explanation.optional(),
  }),
});
